import React, { useEffect, useState } from "react";
import { ShieldCheck, KeyRound, Copy, Trash2, Plus, Loader2 } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import {
  listPasskeys,
  mfaDisable,
  mfaSetup,
  mfaStatus,
  mfaVerify,
  passkeyDelete,
  passkeyRegisterBegin,
  passkeyRegisterComplete,
  passkeysConfig,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { startRegistration } from "@simplewebauthn/browser";

export default function Security() {
  const { toast } = useToast();

  // MFA state
  const [mfa, setMfa] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [working, setWorking] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Passkeys state
  const [pkConfig, setPkConfig] = useState(null);
  const [passkeys, setPasskeys] = useState([]);
  const [label, setLabel] = useState("");
  const [pkBusy, setPkBusy] = useState(false);

  const reload = async () => {
    const [m, c, p] = await Promise.all([mfaStatus(), passkeysConfig(), listPasskeys().catch(() => [])]);
    setMfa(m);
    setPkConfig(c);
    setPasskeys(p || []);
  };
  useEffect(() => { reload(); }, []);

  const startMfa = async () => {
    setWorking(true);
    try {
      const s = await mfaSetup();
      setSetup(s);
    } catch (err) {
      toast({ title: "Could not start setup", description: err?.response?.data?.detail, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const confirmMfa = async () => {
    setWorking(true);
    try {
      const r = await mfaVerify(code);
      setRecoveryCodes(r.recovery_codes);
      setSetup(null);
      setCode("");
      await reload();
      toast({ title: "Two-factor enabled" });
    } catch (err) {
      toast({ title: "Wrong code", description: err?.response?.data?.detail, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const disableMfa = async () => {
    const c = prompt("Enter your current 6-digit code to disable MFA");
    if (!c) return;
    try {
      await mfaDisable(c);
      toast({ title: "Two-factor disabled" });
      await reload();
    } catch (err) {
      toast({ title: "Could not disable", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const addPasskey = async () => {
    if (!pkConfig?.available) {
      toast({ title: "Passkeys not configured on server" });
      return;
    }
    setPkBusy(true);
    try {
      const opts = await passkeyRegisterBegin();
      const credential = await startRegistration(opts);
      await passkeyRegisterComplete(label || "Passkey", credential);
      setLabel("");
      await reload();
      toast({ title: "Passkey added" });
    } catch (err) {
      toast({ title: "Could not add passkey", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setPkBusy(false);
    }
  };

  const removePasskey = async (id) => {
    if (!confirm("Remove this passkey?")) return;
    await passkeyDelete(id);
    await reload();
  };

  return (
    <>
      <PageHeader title="Security" subtitle="Two-factor authentication and passkeys." />
      <PageBody>
        <div className="space-y-8 max-w-3xl">
          {/* MFA */}
          <section data-testid="mfa-card" className="border border-border bg-card rounded-md p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center"><ShieldCheck size={18} className="text-primary" /></div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="serif text-lg text-primary">Two-factor authentication</div>
                  {mfa?.enabled && <span className="status-pill" style={{ backgroundColor: "hsl(145 50% 95%)", color: "hsl(145 65% 22%)" }}>Active</span>}
                </div>
                <p className="text-sm text-muted-foreground mt-1">Use an authenticator app (1Password, Google Authenticator, Authy) for a 6-digit code on every sign-in.</p>

                {recoveryCodes && (
                  <div data-testid="recovery-codes" className="mt-4 rounded-sm border border-secondary/40 bg-secondary/10 px-4 py-3">
                    <div className="font-medium text-sm mb-2">Save these recovery codes</div>
                    <div className="grid grid-cols-2 gap-1 mono text-xs">
                      {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast({ title: "Copied" }); }} className="mt-3 inline-flex items-center gap-1 text-xs underline">
                      <Copy size={12} /> Copy all
                    </button>
                  </div>
                )}

                {!mfa?.enabled && !setup && (
                  <button data-testid="mfa-setup-btn" onClick={startMfa} disabled={working} className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
                    {working ? <Loader2 size={14} className="animate-spin" /> : null} Enable two-factor
                  </button>
                )}

                {setup && (
                  <div className="mt-5 grid sm:grid-cols-2 gap-5 items-start">
                    <div>
                      <img src={setup.qr_data_url} alt="QR" className="w-44 h-44 border border-border rounded-sm bg-white" />
                      <div className="text-[11px] text-muted-foreground mt-2">Or enter the secret manually:</div>
                      <div className="mono text-xs mt-1 break-all">{setup.secret}</div>
                    </div>
                    <div>
                      <label className="block">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Enter 6-digit code</span>
                        <input data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value)} maxLength={10} className="td-input mt-1.5 mono text-lg tracking-widest" placeholder="123456" />
                      </label>
                      <button data-testid="mfa-confirm-btn" onClick={confirmMfa} disabled={working || code.length < 6} className="mt-3 w-full rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium disabled:opacity-60">
                        {working ? "Verifying…" : "Verify and enable"}
                      </button>
                    </div>
                  </div>
                )}

                {mfa?.enabled && (
                  <div className="mt-4">
                    <div className="text-xs text-muted-foreground">Recovery codes remaining: <span className="mono">{mfa.recovery_codes_remaining}</span></div>
                    <button data-testid="mfa-disable-btn" onClick={disableMfa} className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive">
                      Disable two-factor
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Passkeys */}
          <section data-testid="passkeys-card" className="border border-border bg-card rounded-md p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center"><KeyRound size={18} className="text-primary" /></div>
              <div className="flex-1">
                <div className="serif text-lg text-primary">Passkeys</div>
                <p className="text-sm text-muted-foreground mt-1">Sign in with Touch ID, Face ID, Windows Hello, or your phone — no password required.</p>
                {!pkConfig?.available && (
                  <div className="mt-4 rounded-sm border border-border bg-accent/40 px-4 py-3 text-xs text-muted-foreground">
                    Passkeys aren't configured on this server (WEBAUTHN_RP_ID / WEBAUTHN_ORIGIN missing).
                  </div>
                )}

                {pkConfig?.available && (
                  <>
                    <div className="mt-5 flex flex-col sm:flex-row gap-3 items-end">
                      <label className="flex-1 block">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Name this passkey</span>
                        <input data-testid="passkey-label-input" value={label} onChange={(e) => setLabel(e.target.value)} className="td-input mt-1.5" placeholder="MacBook Touch ID" />
                      </label>
                      <button data-testid="passkey-add-btn" onClick={addPasskey} disabled={pkBusy} className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
                        {pkBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add passkey
                      </button>
                    </div>

                    {passkeys.length > 0 && (
                      <div className="mt-6 border-t border-border pt-4 space-y-2">
                        {passkeys.map((p) => (
                          <div key={p.id} className="flex items-center justify-between gap-3 py-2">
                            <div>
                              <div className="text-sm font-medium">{p.label}</div>
                              <div className="text-[11px] text-muted-foreground mono mt-0.5">added {new Date(p.created_at).toLocaleDateString()} · last used {p.last_used_at ? new Date(p.last_used_at).toLocaleDateString() : "never"}</div>
                            </div>
                            <button onClick={() => removePasskey(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </PageBody>
      <style>{`
        .td-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; padding: 10px 12px; font-size: 14px; color: hsl(var(--foreground)); }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </>
  );
}
